// Shared message-attachment plumbing.
//
// Used by:
//   - apps/retailer/src/pages/messages/page.ts (compose modal + thread reply)
//   - apps/retailer/src/shell/customer-compose-modal.ts (customer record popup)
//   - any other compose surface that ships in future
//
// Server upload route: POST /api/message-attachments/upload (multipart/form-data,
// field name "files"). The browser must set its own multipart boundary, so we
// don't go through ctx.api (which forces application/json) — direct fetch only.
//
// Accepted MIME types (UI-side restriction; server allowlist is wider):
//   image/jpeg, image/png, image/webp, application/pdf
// Max size per file: 10 MB.

export interface MessageAttachment {
  id: string;
  url: string;
  name: string;
  mimeType: string;
  size: number;
  kind: "image" | "pdf" | "other";
}

export const ATTACH_ACCEPT_TYPES = [
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
] as const;

export const ATTACH_ACCEPT_ATTR =
  "image/jpeg,image/png,image/webp,application/pdf";

export const ATTACH_MAX_BYTES = 10 * 1024 * 1024;

export function attachmentKindOf(mime: string): MessageAttachment["kind"] {
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  return "other";
}

export function formatAttachmentSize(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

export function normaliseAttachments(raw: unknown): MessageAttachment[] {
  if (!Array.isArray(raw)) return [];
  const out: MessageAttachment[] = [];
  for (const a of raw) {
    if (!a || typeof a !== "object") continue;
    const r = a as Record<string, unknown>;
    const id   = typeof r.id   === "string" ? r.id   : "";
    const url  = typeof r.url  === "string" ? r.url  : "";
    const name = typeof r.name === "string" ? r.name : "";
    const mime = typeof r.mimeType === "string" ? r.mimeType : "";
    const size = typeof r.size === "number" ? r.size : 0;
    if (!id || !url) continue;
    out.push({ id, url, name: name || id, mimeType: mime, size, kind: attachmentKindOf(mime) });
  }
  return out;
}

interface UploadResponse {
  ok: boolean;
  attachments?: MessageAttachment[];
  error?: string;
}

/** Upload one or more files to /api/message-attachments/upload. Throws with a
 *  user-friendly message on validation or transport failure — caller toasts. */
export async function uploadMessageAttachments(files: File[]): Promise<MessageAttachment[]> {
  if (files.length === 0) return [];
  for (const f of files) {
    if (!ATTACH_ACCEPT_TYPES.includes(f.type as (typeof ATTACH_ACCEPT_TYPES)[number])) {
      throw new Error(`${f.name}: only JPG, PNG, WebP or PDF files are allowed.`);
    }
    if (f.size > ATTACH_MAX_BYTES) {
      throw new Error(`${f.name} is over the 10 MB limit.`);
    }
  }
  const form = new FormData();
  for (const f of files) form.append("files", f, f.name);
  const res = await fetch("/api/message-attachments/upload", {
    method: "POST",
    credentials: "include",
    body: form,
  });
  if (!res.ok) {
    let detail = "";
    try { detail = (await res.text()).slice(0, 200); } catch { /* ignore */ }
    throw new Error(detail || `Upload failed (HTTP ${res.status}).`);
  }
  const json = (await res.json()) as UploadResponse;
  if (!json.ok || !Array.isArray(json.attachments)) {
    throw new Error(json.error || "Upload failed.");
  }
  return normaliseAttachments(json.attachments);
}

function escAttr(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

/** Render a single "chip" for a queued attachment (image preview or file icon
 *  + name + size + remove × button). Caller styles via `.msg-attach-chip*`. */
export function renderAttachChipHtml(a: MessageAttachment): string {
  const preview = a.kind === "image"
    ? `<img src="${escAttr(a.url)}" alt="${escAttr(a.name)}" />`
    : `<span class="msg-attach-chip__icon" aria-hidden="true">📄</span>`;
  const sizeLabel = formatAttachmentSize(a.size);
  return `
    <div class="msg-attach-chip">
      ${preview}
      <span class="msg-attach-chip__name" title="${escAttr(a.name)}">${escAttr(a.name)}</span>
      ${sizeLabel ? `<span class="msg-attach-chip__size">${escAttr(sizeLabel)}</span>` : ""}
      <button type="button" class="msg-attach-chip__remove"
              data-role="attach-remove"
              data-attach-id="${escAttr(a.id)}"
              aria-label="Remove ${escAttr(a.name)}">×</button>
    </div>
  `;
}

/** Wrap a list of chips in the tray container. `hidden` if the list is empty.
 *  Returns a full <div> element rather than just innerHTML so callers can swap
 *  the tray contents cleanly. */
export function attachmentTrayHtml(items: MessageAttachment[]): string {
  if (items.length === 0) return "";
  return items.map(renderAttachChipHtml).join("");
}
